import { useState, useRef, useEffect } from "react";

interface Factory {
  name: string;
  flag: string;
  city: string;
  avatar: string;
  verified: boolean;
  responseTime: string;
}

interface Props {
  factory: Factory;
  onClose: () => void;
}

interface Message {
  id: string;
  sender: "buyer" | "factory";
  text: string;
  time: string;
  type?: "text" | "quote";
  quoteData?: { product: string; unitPrice: string; moq: string; leadTime: string };
}

const initialMessages: Message[] = [
  {
    id: "m1",
    sender: "factory",
    text: "Hello! Thank you for your interest. We are a verified manufacturer with 12+ years of export experience. How can we help you today?",
    time: "10:02 AM",
  },
  {
    id: "m2",
    sender: "factory",
    text: "We offer OEM/ODM services, custom branding, and private label. Our minimum order is $8,000 USD. Feel free to ask about any product!",
    time: "10:03 AM",
  },
];

const quickReplies = [
  "What is your MOQ?",
  "Can I order a sample first?",
  "Do you offer custom branding?",
  "What are your payment terms?",
  "How long is the lead time?",
  "Can you send a price list?",
];

const factoryAutoReplies: Record<string, string> = {
  "moq": "Our minimum order is $8,000 USD per product. For containers, we offer 20ft, 40ft, and 40ft HC options. Would you like a detailed quote?",
  "sample": "Yes! We offer sample orders before bulk. Sample cost is typically $50-$300 depending on the product, and it is deducted from your first bulk order. Lead time for samples is 5-10 days.",
  "branding": "Absolutely! We offer full custom branding — your logo, packaging design, and brand name on all products. Private label is available from MOQ.",
  "payment": "We accept Wire Transfer (T/T), Letter of Credit (L/C), and PayPal for orders under $50,000. We typically require 30% deposit and 70% before shipment.",
  "lead time": "Sample lead time is 5-10 days. Bulk order lead time is 25-45 days depending on quantity and customization. We can expedite for urgent orders.",
  "price": "I will send you our full price list right away! Please share your target quantity and destination country so I can include shipping estimates.",
};

function getAutoReply(text: string): string {
  const lower = text.toLowerCase();
  for (const [key, reply] of Object.entries(factoryAutoReplies)) {
    if (lower.includes(key)) return reply;
  }
  return "Thank you for your message! Our sales team will respond within " + "2 hours. You can also request a formal quote by clicking the 'Request Quote' button below.";
}

export default function FactoryMessagingModal({ factory, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ product: "", qty: "", country: "", notes: "" });
  const [quoteSent, setQuoteSent] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const newMsg: Message = { id: `m${Date.now()}`, sender: "buyer", text, time: now };
    setMessages((prev) => [...prev, newMsg]);
    setInput("");
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const reply = getAutoReply(text);
      setMessages((prev) => [...prev, { id: `m${Date.now()}r`, sender: "factory", text: reply, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
    }, 1400);
  };

  const sendQuoteRequest = () => {
    if (!quoteForm.product || !quoteForm.qty) return;
    const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const quoteMsg: Message = {
      id: `m${Date.now()}q`,
      sender: "buyer",
      text: "",
      time: now,
      type: "quote",
      quoteData: { product: quoteForm.product, unitPrice: "TBD", moq: quoteForm.qty, leadTime: "TBD" },
    };
    setMessages((prev) => [...prev, quoteMsg]);
    setShowQuoteForm(false);
    setQuoteSent(true);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [...prev, {
        id: `m${Date.now()}r`,
        sender: "factory",
        text: `Thank you for your quote request for "${quoteForm.product}"! We will prepare a formal quotation with unit price, MOQ, lead time, and shipping cost to ${quoteForm.country || "your country"} within 24 hours. Please check your email.`,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      }]);
    }, 1800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg flex flex-col" style={{ height: "85vh", maxHeight: "680px" }}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <img src={factory.avatar} alt={factory.name} className="w-11 h-11 rounded-xl object-cover" />
            <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-gray-900 text-sm truncate">{factory.name}</span>
              {factory.verified && (
                <span className="flex items-center gap-0.5 bg-green-100 text-green-700 text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">
                  <i className="ri-shield-check-fill text-xs" /> Verified
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400">{factory.flag} {factory.city} · Responds {factory.responseTime}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowQuoteForm(!showQuoteForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg cursor-pointer whitespace-nowrap text-white"
              style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)" }}
            >
              <i className="ri-file-list-3-line" /> Request Quote
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer text-gray-400 flex-shrink-0">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Quote Form Panel */}
        {showQuoteForm && (
          <div className="px-5 py-4 border-b border-amber-100 flex-shrink-0" style={{ background: "linear-gradient(135deg, #FFF8E1, #FFFDE7)" }}>
            <p className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
              <i className="ri-file-list-3-line" /> Request a Formal Quote
            </p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input value={quoteForm.product} onChange={(e) => setQuoteForm({ ...quoteForm, product: e.target.value })} placeholder="Product name *" className="border border-amber-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-amber-400 bg-white" />
              <input value={quoteForm.qty} onChange={(e) => setQuoteForm({ ...quoteForm, qty: e.target.value })} placeholder="Quantity / MOQ *" className="border border-amber-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-amber-400 bg-white" />
              <input value={quoteForm.country} onChange={(e) => setQuoteForm({ ...quoteForm, country: e.target.value })} placeholder="Destination country" className="border border-amber-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-amber-400 bg-white" />
              <input value={quoteForm.notes} onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })} placeholder="Special requirements" className="border border-amber-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-amber-400 bg-white" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowQuoteForm(false)} className="flex-1 py-2 border border-gray-200 text-gray-600 text-xs font-bold rounded-lg cursor-pointer">Cancel</button>
              <button onClick={sendQuoteRequest} disabled={!quoteForm.product || !quoteForm.qty} className="flex-1 py-2 text-white text-xs font-bold rounded-lg cursor-pointer disabled:opacity-50" style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)" }}>
                Send Quote Request
              </button>
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender === "buyer" ? "justify-end" : "justify-start"} gap-2`}>
              {msg.sender === "factory" && (
                <img src={factory.avatar} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0 mt-1" />
              )}
              <div className={`max-w-xs ${msg.sender === "buyer" ? "items-end" : "items-start"} flex flex-col`}>
                {msg.type === "quote" && msg.quoteData ? (
                  <div className="rounded-2xl p-3 border border-amber-200 text-xs" style={{ background: "linear-gradient(135deg, #FFF8E1, #FFFDE7)" }}>
                    <p className="font-bold text-amber-900 mb-2 flex items-center gap-1"><i className="ri-file-list-3-line" /> Quote Request Sent</p>
                    <p className="text-gray-700"><strong>Product:</strong> {msg.quoteData.product}</p>
                    <p className="text-gray-700"><strong>Quantity:</strong> {msg.quoteData.moq}</p>
                  </div>
                ) : (
                  <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.sender === "buyer" ? "text-white rounded-br-sm" : "bg-gray-100 text-gray-800 rounded-bl-sm"}`}
                    style={msg.sender === "buyer" ? { background: "linear-gradient(135deg, #B8860B, #DAA520)" } : {}}>
                    {msg.text}
                  </div>
                )}
                <span className="text-xs text-gray-400 mt-1 px-1">{msg.time}</span>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex items-center gap-2">
              <img src={factory.avatar} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
              <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Quick Replies */}
        <div className="px-5 py-2 flex gap-2 overflow-x-auto flex-shrink-0 border-t border-gray-50">
          {quickReplies.map((qr) => (
            <button
              key={qr}
              onClick={() => sendMessage(qr)}
              className="flex-shrink-0 px-3 py-1.5 bg-gray-50 border border-gray-200 text-gray-600 text-xs font-medium rounded-full hover:border-amber-300 hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {qr}
            </button>
          ))}
        </div>

        {/* Input */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-3 flex-shrink-0">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
            placeholder="Type a message..."
            className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim()}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white cursor-pointer disabled:opacity-40 flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)" }}
          >
            <i className="ri-send-plane-fill text-base" />
          </button>
        </div>
      </div>
    </div>
  );
}
